import React from "react";
import PropTypes from "prop-types";
import { formatMass } from "@signolteam/shared/units-of-measure";
import AppUtils from "@src/utils/appUtils";
import { useDCContext } from "@src/context/dcContext";
import {
  AnimateNumberCount,
  ImageWrapper,
  ImageWithRetry,
} from "@src/components/atoms";
import CardContainer from "./CardContainer";
import VerticalLayout from "./VerticalLayout";
import HorizontalLayout from "./HorizontalLayout";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";
import DonationAmount, {
  DonationAmountOperator,
} from "../atoms/DonationAmount";

/** Display Fuel, CO₂, or Donation savings */
const SavingsCard = ({
  industry,
  /** customerCode currently only used for mode="donations" to get Charity image */
  customerCode = undefined,
  /** @type "co2"|"co2-teal"|"co2-primary"|"fuel"|"donations" */
  mode = "co2",
  value = 0,
  layout = "vertical",
}) => {
  const { getDynamicReplacement, existsDynamicReplacements } = useDCContext();

  let imageUrl, text, color, formattedValue;
  switch (mode) {
    case "co2":
    case "co2-teal":
    case "co2-primary": {
      color = mode === "co2-teal" ? "teal-100" : "primary-100";
      imageUrl = getDynamicReplacement(
        "captain__events__behaviour__swiper__co2_icon",
        {},
        "https://signol-email-images.s3.eu-west-2.amazonaws.com/CARBON_SAVED.png",
        true,
      );
      text = getDynamicReplacement(
        "captain__events__total_carbon_savings",
        {},
        "Total CO₂ savings:",
        true,
      );
      formattedValue = formatMass(industry, value).formattedValue;
      break;
    }
    case "fuel": {
      color = "yellow-100";
      imageUrl = getDynamicReplacement(
        "captain__events__behaviour__swiper__fuel_icon",
        {},
        "https://signol-email-images.s3.eu-west-2.amazonaws.com/FUEL_SAVED.png",
        true,
      );
      text = getDynamicReplacement(
        "captain__events__total_fuel_savings",
        {},
        "Total fuel savings:",
        true,
      );
      formattedValue = formatMass(industry, value).formattedValue;
      break;
    }
    case "donations": {
      color = "yellow-200";
      imageUrl = getDynamicReplacement(
        existsDynamicReplacements(
          `charities__${customerCode?.toLowerCase()}__small_logo`,
        )
          ? `charities__${customerCode.toLowerCase()}__small_logo`
          : `captain__events__behaviour__swiper__donations_icon`,
        {},
        "https://signol-email-images.s3.eu-west-2.amazonaws.com/ImpactPage_Donations.svg",
        true,
      );
      text = getDynamicReplacement(
        existsDynamicReplacements(
          `charities__${customerCode?.toLowerCase()}__impact_page_total_donations_text`,
        )
          ? `charities__${customerCode.toLowerCase()}__impact_page_total_donations_text`
          : "captain__events__total_donation_savings",
        {},
      );
      formattedValue = DonationAmountOperator({ amount: value });
      console.debug("case donations", { value, formattedValue });
      break;
    }
  }

  const isHorizontal = layout === "horizontal";
  const Container = isHorizontal ? HorizontalLayout : VerticalLayout;

  return (
    <CardContainer
      bgColor={color}
      padding={isHorizontal ? 1 : 4}
      shadow={false}
    >
      <Container gap={3} className="@container">
        {/* child 1  - Savings icon */}
        <ImageWithRetry src={imageUrl} alt="" className="h-16 w-auto mx-auto" />

        {/* child 2 - Statement with number */}
        <div
          className={
            isHorizontal ? "text-left @sm:text-center mr-8" : "text-center"
          }
        >
          <div className="text-sm md:text-base">{text}</div>
          <div className="font-bold text-2xl my-1">
            <AnimateNumberCount total={formattedValue} durationMS={1000} />
          </div>
        </div>
      </Container>
    </CardContainer>
  );
};

export const savingsCardControls = {
  industry: COMMON_CONTROLS.industry,
  customerCode: COMMON_CONTROLS.customerCode,
  mode: {
    propType: PropTypes.oneOf([
      "fuel",
      "co2",
      "co2-primary",
      "co2-teal",
      "donations",
    ]),
    description: "The type of saving value",
    controlType: "select",
    options: ["fuel", "co2", "co2-primary", "co2-teal", "donations"],
    defaultValue: "co2",
  },
  value: {
    propType: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
    description:
      "Input Value (should be a number, but can be a string if mode=donations).",
    controlType: "number",
  },
  layout: {
    propType: PropTypes.oneOf(["vertical", "horizontal"]),
    description: "display vertically or horizontally",
    controlType: "select",
    options: ["vertical", "horizontal"],
    defaultValue: "vertical",
  },
};

SavingsCard.propTypes = AppUtils.getPropTypeFromControls(savingsCardControls);

export default SavingsCard;
